// The session-long cache of curated exercise text, lifted out of
// `ExerciseDbService` (#475).
//
// Curated exercise names and descriptions live in the `$text/` keyed-text store
// so they can be translated (#4210). Reading three extra documents per exercise
// inside the loops that load a workout would be far too slow, so the whole
// catalog is read once and kept as a pair of maps.
//
// The cache is its OWN object because its state belongs to the signed-in
// session, while `ExerciseDbService` lives for as long as the app does. Signing
// out throws this object away and puts a fresh one in its place, so a read
// started by the previous user completes into an object nobody holds any more
// and cannot write over the next user's state. That used to be a generation
// counter compared in two places and threaded through a reporting helper; the
// object shape makes the same guarantee by construction.
//
// Dependencies come in through the constructor and are imported as TYPES only,
// so this module loads under plain node in its unit test — the same split
// `backlog-cache.ts` and `text-locale.ts` use to keep testable logic out of an
// injectable service.

import type { DatabaseWrapper, DocWatcher } from 'services/database-wrapper';
import type { LoggingService } from './logging.service';
import {
  ACTIVE_TEXT_SEGMENT,
  ExerciseTextMaps,
  ExerciseTextReadCounts,
  ExerciseTextRereadIntervals,
  isExerciseTextUsable,
  readExerciseTextMapsAsync,
  shouldRereadExerciseText,
  tryExerciseTextKeyFromDocId,
} from './text-locale';

// An empty pair, returned when the `$text/` read fails. Every reader then falls
// back to the exercise's inline text. For a custom exercise that is its only
// text. A curated exercise has had none since #4285, so it shows no name until
// the retry below succeeds; the failure itself is reported once. Built
// fresh each time rather than shared as a module constant, so no caller can
// reach a map that another failed read is also holding.
function noExerciseText(): ExerciseTextMaps {
  return { localized: new Map(), english: new Map() };
}

/** The one part of `LoggingService` this cache uses. */
export type ExerciseTextProblemReporter = Pick<LoggingService, 'trackSilentProblem'>;

// How long the two "wait before reading again" rules wait.
//
// The RETRY interval governs an unusable read: long enough that a device still
// syncing does not re-scan the catalog once per exercise, short enough that a
// transient failure heals within a page or two.
//
// The STALE window governs a cache the DocWatcher marked changed. It is short —
// an arriving translation should show up almost at once — but not zero, because
// documents arrive in batches and the first sync delivers 1584 of them.
export const EXERCISE_TEXT_REREAD_INTERVALS: ExerciseTextRereadIntervals = {
  retryIntervalMs: 30_000,
  staleCoalesceMs: 2_000,
};

/**
 * One signed-in session's cached copy of the curated exercise text.
 *
 * Owned by `ExerciseDbService`, which holds exactly one at a time and replaces
 * it at sign-out. Nothing outside that service should build one.
 */
export class ExerciseTextCache {
  private cachedRead: Promise<ExerciseTextMaps> | undefined;
  // when the cached read COMPLETED, and whether it produced anything usable —
  // together they decide when an unusable read may be tried again. Completion,
  // not start: measuring from the start makes a slow read look unusable.
  private readAtMs = 0;
  private wasUsable = false;
  // a read already on its way. Without it a slow first read looks exactly like
  // "the last read was unusable", and a device mid-sync would stack a fresh
  // catalog scan every retry interval — the very case the throttle exists for.
  private readInFlight = false;
  // an `@exercise:` document changed since the cached read started, so the cache
  // is refreshed on the short coalescing window instead of the retry interval
  private stale = false;
  // persistent watcher — lives as long as this cache does and MARKS the maps
  // stale when a `$text/@exercise:` document arrives by sync, so a translation
  // that replicates mid-session is picked up on the next read
  private watcher: DocWatcher | undefined;
  // report each unusable-read problem once per session, not once per retry — an
  // unusable read is retried on the throttle for as long as it keeps coming back
  // empty, and a report per retry would bury the signal it exists to give. Keyed
  // BY NAME, so a read failure is still reported after an empty-language report,
  // and vice versa — they are different faults.
  private reportedProblems = new Set<string>();

  /**
   * @param getDatabase reads the shared database LAZILY. It cannot be handed in
   *   as a value: `CommonDbService.database` is undefined until the service has
   *   initialized, and the first read here can happen either side of that.
   * @param intervals overridable so a unit test does not have to wait out the
   *   real ones; nothing in the app passes anything but the default.
   */
  constructor(
    private readonly getDatabase: () => DatabaseWrapper,
    private readonly logging: ExerciseTextProblemReporter,
    private readonly intervals: ExerciseTextRereadIntervals = EXERCISE_TEXT_REREAD_INTERVALS,
  ) { }

  /**
   * The curated-exercise text maps for this session, read once and cached.
   *
   * A failed or empty read is NOT allowed to break exercise loading: it degrades
   * to the inline fields (none on a curated exercise since #4285, so its name is
   * blank until a retry succeeds) and is reported, because after
   * #4210 Phase C every curated exercise has documents, so an empty English read
   * means they never replicated to this device or the read did not work.
   *
   * Two rules decide when it reads again, both in `shouldRereadExerciseText` and
   * both timed by the intervals this cache was built with:
   *
   *  - an UNUSABLE result is retried, but no more often than `retryIntervalMs`.
   *    `getByIdHeadAsync` THROWS while the database is recovering (it used to
   *    answer with an empty list), and `readMapsAsync`'s catch turns that into
   *    `noExerciseText()` — so an unusable result still reaches here, and keeping
   *    it would strand a non-English user on English for the whole session with
   *    nothing to clear it. Retrying on every call would be just as bad the other
   *    way: this method runs once per exercise inside loops.
   *  - a STALE cache — one the watcher marked because a document arrived — is
   *    refreshed after `staleCoalesceMs`, a short window that collapses a burst
   *    of arriving documents into one re-read instead of one per batch.
   *
   * "Unusable" covers BOTH queries — see `isExerciseTextUsable`.
   */
  async getMapsAsync(): Promise<ExerciseTextMaps> {
    const shouldReread = shouldRereadExerciseText({
      hasCachedRead: this.cachedRead !== undefined,
      isReadInFlight: this.readInFlight,
      isStale: this.stale,
      lastReadWasUsable: this.wasUsable,
      msSinceLastRead: Date.now() - this.readAtMs,
    }, this.intervals);
    if (shouldReread) {
      this.readInFlight = true;
      // clear the stale mark HERE, at the start: the read about to run sees the
      // current documents. A document arriving while it runs sets the mark
      // again, so that change is not lost either.
      this.stale = false;
      // record the outcome where the read COMPLETES, not where each caller
      // happens to await it — the interval is measured from the end of the last
      // read. A read that outlived its session still resolves for whoever
      // awaited it and writes these fields, but by then this whole object has
      // been discarded, so it writes nothing the next session can see.
      this.cachedRead = this.readMapsAsync().then(maps => {
        this.wasUsable = isExerciseTextUsable(maps);
        this.readAtMs = Date.now();
        this.readInFlight = false;
        return maps;
      });
    }
    return this.cachedRead;
  }

  /**
   * Say that the cached maps no longer match the stored documents, so the next
   * read refreshes them after the short coalescing window.
   *
   * Called by the sync watcher below, and by the curated-exercise save path
   * after it writes English `$text/` documents of its own.
   */
  markStale(): void {
    this.stale = true;
  }

  /**
   * Throw this cache away — the signed-in session it belongs to has ended.
   *
   * The report gate is cleared as well as the watcher destroyed. A read that is
   * still in flight reports into THIS object after the discard, and clearing the
   * gate is what keeps that report going out: the problem really happened, and
   * the next user's own gate is a different set entirely, so nothing is
   * suppressed for them either way.
   */
  discard(): void {
    this.watcher?.destroy();
    this.watcher = undefined;
    this.reportedProblems.clear();
  }

  // NEVER rejects. The caller caches the promise it returns, so a rejection
  // would be re-thrown to every reader for the rest of the session; an
  // unusable result instead reports itself and is retried on the interval.
  private async readMapsAsync(): Promise<ExerciseTextMaps> {
    const malformedDocIds: string[] = [];
    const counts: ExerciseTextReadCounts = { englishCount: 0, localizedCount: 0 };
    try {
      // create the watcher lazily, on the first read: the database is not open
      // before the owning service has initialized. If this throws, the read
      // below is skipped, the result is unusable, and the retry re-attempts BOTH.
      if (!this.watcher) {
        this.watcher = this.getDatabase().createDocWatcher(changes => {
          // only an `@exercise:` document can change these maps. `notifyNew`
          // BROADCASTS every untracked arriving document to every watcher, so
          // without this filter the whole catalog would be re-read once per sync
          // batch during the initial sync of the database.
          if (!changes.some(change => tryExerciseTextKeyFromDocId(change.id))) {
            return;
          }
          // MARK the cache stale rather than dropping it. Dropping it while a
          // read is in flight would leave nothing to return to a caller that
          // arrives in that window, because an in-flight read is never started
          // twice — it IS the freshest thing there is.
          this.markStale();
        });
      }
      const watcher = this.watcher;
      const maps = await readExerciseTextMapsAsync(
        prefix => this.getDatabase().getByIdHeadAsync(prefix, watcher),
        ACTIVE_TEXT_SEGMENT, malformedDocIds, counts);

      // every id came from an `@exercise:` prefix query, so one that does not
      // parse is a corrupt document rather than a normal state
      if (malformedDocIds.length > 0) {
        this.reportProblemOnce('ExerciseTextMalformedDocId', {
          count: malformedDocIds.length,
          docIds: malformedDocIds.slice(0, 10).join(', '),
        });
      }
      // An id-prefix query USED TO return an empty list rather than failing when
      // the database was unavailable, which made "no documents" and "the read did
      // not work" look identical from here. It now throws
      // `DatabaseUnavailableError`, so that ambiguity is gone. The report below
      // still earns its keep: every curated exercise has documents, so a read
      // that genuinely RAN and found none means they never replicated.
      if (counts.englishCount === 0) {
        this.reportProblemOnce('ExerciseTextEnglishEmpty', {});
      } else if (counts.localizedCount === 0) {
        this.reportProblemOnce('ExerciseTextLocalizedEmpty', {
          segment: ACTIVE_TEXT_SEGMENT,
          englishCount: counts.englishCount,
        });
      }
      return maps;
    } catch (err) {
      // exercise text must never block exercise loading — fall back to the
      // inline fields, and read again after the retry interval
      this.reportProblemOnce('ExerciseTextReadFailed', {
        segment: ACTIVE_TEXT_SEGMENT,
        message: err?.message,
        code: err?.status ?? err?.name,
      });
      return noExerciseText();
    }
  }

  /**
   * Report curated exercises that hydration left without a name (#4285).
   *
   * A curated exercise has no inline text any more, so a blank name means its
   * English `$text/` document is missing, and the user sees a nameless exercise.
   * The loaders call this on every read, so each exercise is reported once per
   * session, and each one separately: two missing documents are two faults.
   */
  reportCuratedExercisesWithoutName(exerciseIds: readonly string[]): void {
    for (const exerciseId of exerciseIds) {
      this.reportProblemOnce('CuratedExerciseTextMissing', { exerciseId }, `CuratedExerciseTextMissing:${exerciseId}`);
    }
  }

  // The unusable-read reports fire on a path that RETRIES, so send each one once
  // per session. The gate belongs to this cache, and this cache belongs to one
  // session — so a read that outlives its session cannot mark a name as
  // already-reported for the user who comes next. `gateKey` defaults to the
  // name; a report about one exercise among many passes its own key instead.
  private reportProblemOnce(name: string, context: Record<string, unknown>, gateKey = name): void {
    if (this.reportedProblems.has(gateKey)) {
      return;
    }
    this.reportedProblems.add(gateKey);
    this.logging.trackSilentProblem(name, context);
  }
}
